import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { testAPI, companyAPI, PassdownDeliveryMode, reportRecipientAPI, ReportRecipient, ReportRecipientCreate, TestDataResponse, departmentAPI } from '../services/api';
import { Department } from '../types/operations';
import ERPSettings from '../components/ERPSettings';
import OperationsTab from '../components/settings-tabs/OperationsTab';
import UsersTab from '../components/settings-tabs/UsersTab';
import TestDataModal from '../components/TestDataModal';
import { usePermissions } from '../hooks/usePermissions';

type TabType = 'operations' | 'erp' | 'company' | 'alerts' | 'users';

const DELIVERY_MODE_OPTIONS: { value: PassdownDeliveryMode; label: string; description: string }[] = [
  {
    value: 'email_only',
    label: 'Email Only',
    description: 'Passdowns are sent via email to configured recipients. No user assignment or acknowledgment required.',
  },
  {
    value: 'assignment_only',
    label: 'Assignment Only',
    description: 'Passdowns are assigned to users who must acknowledge them. No email is sent.',
  },
  {
    value: 'email_and_assignment',
    label: 'Email + Assignment',
    description: 'Passdowns can be sent via email and/or assigned to users. Most flexible option.',
  },
];

const Settings: React.FC = () => {
  const { vellrixUser } = useAuth();
  const { isAdmin } = usePermissions();
  const [activeTab, setActiveTab] = useState<TabType>('operations');
  const [brandSettings, setBrandSettings] = useState({
    companyName: '',
    brandColor: '#1f2937',
    logoUrl: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  // Passdown settings state
  const [passdownDeliveryMode, setPassdownDeliveryMode] = useState<PassdownDeliveryMode>('email_only');
  const [passdownSettingsLoading, setPassdownSettingsLoading] = useState(true);
  const [passdownSettingsSaving, setPassdownSettingsSaving] = useState(false);
  const [passdownSettingsError, setPassdownSettingsError] = useState<string | null>(null);
  const [passdownSettingsSuccess, setPassdownSettingsSuccess] = useState(false);

  // Test data generation state
  const [showTestDataModal, setShowTestDataModal] = useState(false);
  const [testDataResult, setTestDataResult] = useState<string | null>(null);

  // Email recipients state
  const [recipients, setRecipients] = useState<ReportRecipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsError, setRecipientsError] = useState<string | null>(null);
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientName, setNewRecipientName] = useState('');
  const [newRecipientRole, setNewRecipientRole] = useState('');
  const [newRecipientDeptId, setNewRecipientDeptId] = useState<number | ''>('');
  const [addRecipientError, setAddRecipientError] = useState<string | null>(null);
  const [addRecipientSaving, setAddRecipientSaving] = useState(false);
  const [recipientDepartments, setRecipientDepartments] = useState<Department[]>([]);

  // Check URL hash for initial tab and listen for changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1) as TabType;
      if (hash && ['operations', 'erp', 'company', 'alerts', 'users'].includes(hash)) {
        setActiveTab(hash);
      }
    };

    // Check hash on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Fetch recipients and departments when alerts tab is active
  useEffect(() => {
    if (activeTab !== 'alerts') return;
    const fetchRecipients = async () => {
      try {
        setRecipientsLoading(true);
        setRecipientsError(null);
        const data = await reportRecipientAPI.list({ active_only: false });
        setRecipients(data);
      } catch (err: any) {
        console.error('Failed to fetch recipients:', err);
        setRecipientsError('Failed to load email recipients');
      } finally {
        setRecipientsLoading(false);
      }
    };
    const fetchDepts = async () => {
      try {
        const depts = await departmentAPI.list();
        setRecipientDepartments(depts);
      } catch (err) {
        console.error('Failed to fetch departments:', err);
      }
    };
    fetchRecipients();
    fetchDepts();
  }, [activeTab]);

  const handleAddRecipient = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newRecipientEmail)) {
      setAddRecipientError('Please enter a valid email address');
      return;
    }
    try {
      setAddRecipientSaving(true);
      setAddRecipientError(null);
      const data: ReportRecipientCreate = {
        email: newRecipientEmail,
        name: newRecipientName || undefined,
        role: newRecipientRole || undefined,
        department_id: newRecipientDeptId ? Number(newRecipientDeptId) : null,
      };
      const created = await reportRecipientAPI.create(data);
      setRecipients(prev => [...prev, created]);
      setNewRecipientEmail('');
      setNewRecipientName('');
      setNewRecipientRole('');
      setNewRecipientDeptId('');
      setShowAddRecipient(false);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setAddRecipientError(typeof detail === 'string' ? detail : 'Failed to add recipient');
    } finally {
      setAddRecipientSaving(false);
    }
  };

  const handleDeleteRecipient = async (recipientId: number) => {
    if (!window.confirm('Remove this email recipient?')) return;
    try {
      await reportRecipientAPI.delete(recipientId);
      setRecipients(prev => prev.filter(r => r.id !== recipientId));
    } catch (err: any) {
      console.error('Failed to delete recipient:', err);
      alert('Failed to remove recipient');
    }
  };

  const handleToggleRecipientActive = async (recipient: ReportRecipient) => {
    try {
      const updated = await reportRecipientAPI.update(recipient.id, { is_active: !recipient.is_active });
      setRecipients(prev => prev.map(r => r.id === recipient.id ? updated : r));
    } catch (err: any) {
      console.error('Failed to update recipient:', err);
    }
  };

  // Fetch passdown settings when alerts tab is active
  useEffect(() => {
    const fetchPassdownSettings = async () => {
      if (activeTab !== 'alerts') return;
      try {
        setPassdownSettingsLoading(true);
        setPassdownSettingsError(null);
        const settings = await companyAPI.getSettings();
        setPassdownDeliveryMode(settings.passdown_delivery_mode);
      } catch (err: any) {
        console.error('Failed to fetch passdown settings:', err);
        const detail = err.response?.data?.detail;
        const errorMsg = Array.isArray(detail)
          ? detail.map((e: any) => e.msg || e).join(', ')
          : (typeof detail === 'string' ? detail : 'Failed to load settings');
        setPassdownSettingsError(errorMsg);
      } finally {
        setPassdownSettingsLoading(false);
      }
    };
    fetchPassdownSettings();
  }, [activeTab]);

  const handleSavePassdownSettings = async () => {
    try {
      setPassdownSettingsSaving(true);
      setPassdownSettingsError(null);
      setPassdownSettingsSuccess(false);
      await companyAPI.updateSettings({ passdown_delivery_mode: passdownDeliveryMode });
      setPassdownSettingsSuccess(true);
      setTimeout(() => setPassdownSettingsSuccess(false), 3000);
    } catch (err: any) {
      console.error('Failed to save passdown settings:', err);
      const detail = err.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((e: any) => e.msg || e).join(', ')
        : (typeof detail === 'string' ? detail : 'Failed to save settings');
      setPassdownSettingsError(errorMsg);
    } finally {
      setPassdownSettingsSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setBrandSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveBranding = async () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      alert('Branding settings will be saved to the backend in the next phase');
    }, 1000);
  };

  const handleTestAPI = async () => {
    try {
      const result = await testAPI.whoAmI();
      alert(`API Test Success! Response: ${JSON.stringify(result, null, 2)}`);
    } catch (error: any) {
      alert(`API Test Failed: ${error.message}`);
    }
  };

  const handleTestDataGenerated = (result: TestDataResponse) => {
    setTestDataResult(
      `Generated test data!\n` +
      `Shift Report ID: ${result.shift_report_id}\n` +
      `Machines: ${result.machines_count}\n` +
      `KPI Violations: ${result.alerts_created}`
    );
  };

  const tabs = [
    { id: 'operations' as TabType, label: 'Operations', icon: '🏭' },
    { id: 'erp' as TabType, label: 'ERP Systems', icon: '🔗' },
    { id: 'company' as TabType, label: 'Company Info', icon: '🏛️' },
    { id: 'alerts' as TabType, label: 'Alerts', icon: '🔔' },
    ...(isAdmin ? [{ id: 'users' as TabType, label: 'Users', icon: '👥' }] : []),
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'operations':
        return <OperationsTab />;

      case 'erp':
        return <ERPSettings />;

      case 'company':
        return (
          <div className="rounded-lg shadow p-6" style={{ background: 'var(--bg-secondary)' }}>
            <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Company Information
            </h2>
            <div className="space-y-4">
              <div>
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Company ID
                </div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {vellrixUser?.company_id}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Admin User
                </div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {vellrixUser?.full_name} ({vellrixUser?.email})
                </div>
              </div>
              <div>
                <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Account Type
                </div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {vellrixUser?.is_company_admin ? 'Company Administrator' : 'User'}
                </div>
              </div>
            </div>

            {/* Branding Settings */}
            <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-md font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Branding
              </h3>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={brandSettings.companyName}
                    onChange={(e) => handleInputChange('companyName', e.target.value)}
                    placeholder="Company Inc."
                    className="w-full px-3 py-2 border rounded-md"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Brand Color (hex)
                  </label>
                  <input
                    type="color"
                    value={brandSettings.brandColor}
                    onChange={(e) => handleInputChange('brandColor', e.target.value)}
                    className="w-full h-10 border rounded-md cursor-pointer"
                    style={{ borderColor: 'var(--border-color)' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Logo URL
                  </label>
                  <input
                    type="url"
                    value={brandSettings.logoUrl}
                    onChange={(e) => handleInputChange('logoUrl', e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border rounded-md"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  />
                </div>
              </div>
              <button
                onClick={handleSaveBranding}
                disabled={isSaving}
                className="px-4 py-2 rounded font-medium disabled:opacity-50"
                style={{
                  background: 'var(--btn-bg)',
                  color: 'var(--btn-text)'
                }}
              >
                {isSaving ? 'Saving...' : 'Save Branding'}
              </button>
            </div>

            {/* API Testing */}
            <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-md font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                API Testing
              </h3>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                Test the backend API connectivity:
              </p>
              <button
                onClick={handleTestAPI}
                className="px-4 py-2 rounded font-medium"
                style={{
                  background: 'var(--btn-bg)',
                  color: 'var(--btn-text)'
                }}
              >
                Test "Who Am I" Endpoint
              </button>
            </div>

            {/* Test Data Generation */}
            <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--border-color)' }}>
              <h3 className="text-md font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Test Data Generator
              </h3>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                Generate sample production data with configurable KPI violations for testing:
              </p>

              {testDataResult && (
                <div className="mb-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm whitespace-pre-line">
                  {testDataResult}
                </div>
              )}

              <button
                onClick={() => setShowTestDataModal(true)}
                className="px-4 py-2 rounded font-medium"
                style={{
                  background: '#059669',
                  color: 'white'
                }}
              >
                Configure & Generate Test Data
              </button>
            </div>
          </div>
        );

      case 'users':
        return isAdmin ? <UsersTab /> : null;

      case 'alerts':
        return (
          <div className="space-y-6">
            {/* Passdown Delivery Mode */}
            <div className="rounded-lg shadow p-6" style={{ background: 'var(--bg-secondary)' }}>
              <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                Passdown Delivery Mode
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                Configure how shift passdowns are delivered to your team.
              </p>

              {passdownSettingsLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span style={{ color: 'var(--text-secondary)' }}>Loading settings...</span>
                </div>
              ) : (
                <>
                  {passdownSettingsError && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                      {passdownSettingsError}
                    </div>
                  )}

                  {passdownSettingsSuccess && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm">
                      Settings saved successfully!
                    </div>
                  )}

                  <div className="space-y-3 mb-4">
                    {DELIVERY_MODE_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                          passdownDeliveryMode === option.value
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                        style={{ background: passdownDeliveryMode === option.value ? undefined : 'var(--bg-primary)' }}
                      >
                        <input
                          type="radio"
                          name="passdownDeliveryMode"
                          value={option.value}
                          checked={passdownDeliveryMode === option.value}
                          onChange={(e) => setPassdownDeliveryMode(e.target.value as PassdownDeliveryMode)}
                          className="mt-1 w-4 h-4 text-blue-600"
                        />
                        <div>
                          <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            {option.label}
                          </div>
                          <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {option.description}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  <button
                    onClick={handleSavePassdownSettings}
                    disabled={passdownSettingsSaving}
                    className="px-4 py-2 rounded font-medium disabled:opacity-50"
                    style={{
                      background: 'var(--btn-bg)',
                      color: 'var(--btn-text)'
                    }}
                  >
                    {passdownSettingsSaving ? 'Saving...' : 'Save Delivery Mode'}
                  </button>
                </>
              )}
            </div>

            {/* Alert Thresholds */}
            <div className="rounded-lg shadow p-6" style={{ background: 'var(--bg-secondary)' }}>
              <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Alert Thresholds
              </h2>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Scrap Alert Threshold (%)
                  </label>
                  <input
                    type="number"
                    defaultValue="5"
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border rounded-md"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Downtime Alert Threshold (minutes)
                  </label>
                  <input
                    type="number"
                    defaultValue="30"
                    min="0"
                    className="w-full px-3 py-2 border rounded-md"
                    style={{
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-color)'
                    }}
                  />
                </div>
              </div>
              <button
                className="px-4 py-2 rounded font-medium"
                style={{
                  background: 'var(--btn-bg)',
                  color: 'var(--btn-text)'
                }}
              >
                Save Thresholds
              </button>
            </div>

            {/* Email Recipients Management */}
            <div className="rounded-lg shadow p-6" style={{ background: 'var(--bg-secondary)' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                    Email Recipients
                  </h2>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Manage who receives passdown and shift report emails.
                  </p>
                </div>
                <button
                  onClick={() => setShowAddRecipient(!showAddRecipient)}
                  className="px-4 py-2 rounded font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  {showAddRecipient ? 'Cancel' : '+ Add Recipient'}
                </button>
              </div>

              {/* Add Recipient Form */}
              {showAddRecipient && (
                <div className="mb-4 p-4 rounded-lg border-2 border-blue-200 dark:border-blue-800" style={{ background: 'var(--bg-primary)' }}>
                  {addRecipientError && (
                    <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm">
                      {addRecipientError}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Email *
                      </label>
                      <input
                        type="email"
                        value={newRecipientEmail}
                        onChange={(e) => setNewRecipientEmail(e.target.value)}
                        placeholder="user@example.com"
                        className="w-full px-3 py-2 border rounded-md text-sm"
                        style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Name
                      </label>
                      <input
                        type="text"
                        value={newRecipientName}
                        onChange={(e) => setNewRecipientName(e.target.value)}
                        placeholder="John Smith"
                        className="w-full px-3 py-2 border rounded-md text-sm"
                        style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Role
                      </label>
                      <input
                        type="text"
                        value={newRecipientRole}
                        onChange={(e) => setNewRecipientRole(e.target.value)}
                        placeholder="Shift Supervisor"
                        className="w-full px-3 py-2 border rounded-md text-sm"
                        style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                        Department Scope
                      </label>
                      <select
                        value={newRecipientDeptId}
                        onChange={(e) => setNewRecipientDeptId(e.target.value ? Number(e.target.value) : '')}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                        style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                      >
                        <option value="">All Departments</option>
                        {recipientDepartments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={handleAddRecipient}
                    disabled={addRecipientSaving || !newRecipientEmail}
                    className="px-4 py-2 rounded font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {addRecipientSaving ? 'Adding...' : 'Add Recipient'}
                  </button>
                </div>
              )}

              {/* Recipients List */}
              {recipientsLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                  <span style={{ color: 'var(--text-secondary)' }}>Loading recipients...</span>
                </div>
              ) : recipientsError ? (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {recipientsError}
                </div>
              ) : recipients.length === 0 ? (
                <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                  <div className="text-3xl mb-2">📧</div>
                  <p>No email recipients configured.</p>
                  <p className="text-sm mt-1">Add recipients above to enable passdown email delivery.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recipients.map((recipient) => (
                    <div
                      key={recipient.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        recipient.is_active
                          ? 'border-gray-200 dark:border-gray-700'
                          : 'border-gray-200 dark:border-gray-700 opacity-50'
                      }`}
                      style={{ background: 'var(--bg-primary)' }}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                            {recipient.email}
                          </span>
                          {!recipient.is_active && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              Inactive
                            </span>
                          )}
                          {recipient.department_name && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                              {recipient.department_name}
                            </span>
                          )}
                          {!recipient.department_id && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                              All Departments
                            </span>
                          )}
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                          {recipient.name && <span>{recipient.name}</span>}
                          {recipient.name && recipient.role && <span> - </span>}
                          {recipient.role && <span>{recipient.role}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleRecipientActive(recipient)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                            recipient.is_active
                              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300'
                              : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300'
                          }`}
                        >
                          {recipient.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleDeleteRecipient(recipient.id)}
                          className="px-3 py-1 rounded text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <section className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Settings
        </h1>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-600'
                  : 'border-transparent hover:border-gray-300'
              }`}
              style={{
                color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-secondary)',
              }}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div>{renderTabContent()}</div>

      {/* Test Data Modal */}
      <TestDataModal
        isOpen={showTestDataModal}
        onClose={() => setShowTestDataModal(false)}
        onGenerated={handleTestDataGenerated}
      />
    </section>
  );
};

export default Settings;
